import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { enqueueSnackbar } from 'notistack';
import loadingPlansService, { NestedPartPayload } from '../../../../services/api/loadingPlansService';
import { fromMatrix4 } from '../editMode.utils';
import {
  buildDecidedPartIds,
  buildPartialRenestPayload,
  DEFAULT_NESTING_BATCH_SIZE,
  flattenPlacableNestedParts,
  getUndecidedPartIds,
  IDENTITY_MATRIX,
  RenestNestedPart,
  sortNestedParts,
} from '../partialRenest.utils';

// Maximum auto-chained empty batches before halting retry loop.
const MAX_CONSECUTIVE_EMPTY_AUTO_BATCHES = 3;

export type RenestTrigger =
  // `payload` is the locked prefix computed at Accept time. A queued edit runs after the plan and
  // layer state were already swapped/truncated, so it must not be recomputed from that later state.
  | { type: 'edit'; editedPartId: string; newTransformMatrix: number[][]; replacedOldPartId?: string | null; editedOrientationIndex?: number; payload?: NestedPartPayload[] }
  | { type: 'lookahead'; isAutoChained?: boolean };

interface UseRenestQueueParams {
  currentLoadingPlan: any;
  updateCurrentLoadingPlan: (updates: any) => void;
  layerData: { [key: number]: { parts: any[]; supportObjects: any[] } };
  currentLayer: number;
  scenePartsMap: { [partId: string]: THREE.Object3D };
  resetSceneForReload: (restoreToPartId?: string) => void;
  currentPartRef: React.MutableRefObject<THREE.Object3D | null>;
  isEditMode: boolean;
  isRenestInFlightRef: React.MutableRefObject<boolean>;
  submitRenestTriggerRef: React.MutableRefObject<((trigger: RenestTrigger) => Promise<void>) | null>;
  lastBatchFirstPartIdRef: React.MutableRefObject<string | null>;
}

export function useRenestQueue({
  currentLoadingPlan,
  updateCurrentLoadingPlan,
  layerData,
  currentLayer,
  scenePartsMap,
  resetSceneForReload,
  currentPartRef,
  isEditMode,
  isRenestInFlightRef,
  submitRenestTriggerRef,
  lastBatchFirstPartIdRef,
}: UseRenestQueueParams) {
  const [isRecalculating, setIsRecalculating] = useState(false);

  const pendingRenestTriggerRef = useRef<RenestTrigger | null>(null);
  const consecutiveEmptyAutoBatchesRef = useRef(0);
  const activePollCancellersRef = useRef<Set<() => void>>(new Set());
  const currentLoadingPlanRef = useRef(currentLoadingPlan);
  currentLoadingPlanRef.current = currentLoadingPlan;
  const isEditModeRef = useRef(false);
  isEditModeRef.current = isEditMode;

  /**
   * Rebuilds the nested-parts list from the scene (so user moves are included) and applies the
   * pending slot swap, if any. The replacement's original entry is dropped so it is not listed
   * twice (once in its old position, once in the slot it now occupies).
   */
  const buildUpdatedNestedParts = useCallback(
    (
      swap: {
        fromPartId: string;
        toPartId: string;
        matrix: THREE.Matrix4;
        orientationIndex?: number;
      } | null
    ): RenestNestedPart[] => {
      const nested: RenestNestedPart[] = currentLoadingPlan?.nestingOutput?.nested_parts ?? [];

      return nested
        .filter((nestedPart) => !swap || nestedPart.part_id !== swap.toPartId || swap.fromPartId === swap.toPartId)
        .map((nestedPart) => {
          const isSwappedSlot = !!swap && (nestedPart.part_id === swap.fromPartId || nestedPart.part_id === swap.toPartId);
          const partId = isSwappedSlot && swap ? swap.toPartId : nestedPart.part_id;
          const matrix = isSwappedSlot && swap ? swap.matrix : scenePartsMap[partId]?.matrix;
          const orientationIndex =
            isSwappedSlot && swap && swap.orientationIndex !== undefined
              ? swap.orientationIndex
              : nestedPart.orientation_index;

          let childItems = nestedPart.child_items;
          if (swap && childItems) {
            childItems = childItems
              .filter((child) => child.part_id !== swap.toPartId || swap.fromPartId === swap.toPartId)
              .map((child) => {
                if (child.part_id === swap.fromPartId || child.part_id === swap.toPartId) {
                  return {
                    ...child,
                    part_id: swap.toPartId,
                    transform_matrix: swap.matrix ? fromMatrix4(swap.matrix) : child.transform_matrix,
                    orientation_index:
                      swap.orientationIndex !== undefined ? swap.orientationIndex : child.orientation_index,
                  };
                }
                return child;
              });
          }

          // Spread first so fields the viewer doesn't track (e.g. pallet child_items) survive.
          return {
            ...nestedPart,
            part_id: partId,
            order_number: nestedPart.order_number,
            assigned_loading_layer: nestedPart.assigned_loading_layer,
            orientation_index: orientationIndex,
            transform_matrix: matrix ? fromMatrix4(matrix) : nestedPart.transform_matrix,
            ...(childItems ? { child_items: childItems } : {}),
          };
        });
    },
    [currentLoadingPlan?.nestingOutput?.nested_parts, scenePartsMap]
  );


  // Polls until this windowed call actually lands. Partial calls never touch `status` (see the
  // backend's isPartial handling), so - unlike the full nest in Step 3 - `updatedAt` advancing is
  // the only signal that this specific call's merge has been applied. Resolves to the updated
  // plan on success, or null on failure/timeout.
  //
  // This is only ever safe to run one at a time (see isRenestInFlightRef/pendingRenestTriggerRef
  // above): `updatedAt` advancing doesn't say WHICH request caused it, so if a second request were
  // ever submitted while this poll was still watching for the first one's completion, this poll
  // could resolve with the SECOND (or a third, etc.) request's result instead of the one it was
  // actually sent for - and vice versa. Each call still gets its own local interval/timeout handles
  // (never a shared ref) purely so an unmount's cleanup can't clear the wrong call's timer.
  const waitForPartialRenestCompletion = useCallback(
    (loadingPlanId: number, requestedBeforeAt?: string): Promise<any | null> => {
      return new Promise((resolve) => {
        const pollIntervalMs = Number(process.env.REACT_APP_NESTING_POLLING_INTERVAL || 5000);
        const timeoutMs = Number(process.env.REACT_APP_NESTING_POLLING_TIMEOUT || 60) * 60 * 1000;

        let intervalId: ReturnType<typeof setInterval> | null = null;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const clearPolling = () => {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          activePollCancellersRef.current.delete(clearPolling);
        };
        activePollCancellersRef.current.add(clearPolling);

        intervalId = setInterval(async () => {
          try {
            const updatedPlan = await loadingPlansService.fetchLoadingPlanById(loadingPlanId);
            if (updatedPlan.updatedAt !== requestedBeforeAt) {
              clearPolling();
              resolve(updatedPlan);
            }
          } catch (pollError) {
            console.error('Failed to fetch loading plan status during recalculation:', pollError);
            clearPolling();
            enqueueSnackbar('Failed to check recalculation status.', { variant: 'error' });
            resolve(null);
          }
        }, pollIntervalMs);

        timeoutId = setTimeout(() => {
          clearPolling();
          enqueueSnackbar('Recalculation timed out. Please try again.', { variant: 'error' });
          resolve(null);
        }, timeoutMs);
      });
    },
    []
  );

  // Builds the windowed nesting payload for this trigger. 'edit': lock the prefix
  // buildPartialRenestPayload computes for the edit. 'lookahead': nothing new is being edited -
  // everything currently confirmed just stays fixed as-is.
  const buildRenestPayload = useCallback(
    (trigger: RenestTrigger, rawNestedParts: RenestNestedPart[]) => {
      if (trigger.type === 'edit' && trigger.payload) {
        return trigger.payload;
      }
      const flatRawNestedParts = flattenPlacableNestedParts(rawNestedParts);
      return trigger.type === 'edit'
        ? buildPartialRenestPayload({
            rawNestedParts,
            layerData,
            currentLayer,
            editedPartId: trigger.editedPartId,
            newTransformMatrix: trigger.newTransformMatrix,
            replacedOldPartId: trigger.replacedOldPartId,
            scenePartsMap,
            editedOrientationIndex: trigger.editedOrientationIndex,
          })
        : flatRawNestedParts.map((part) => ({
            part_id: part.part_id,
            order_number: part.order_number ?? 0,
            assigned_loading_layer: part.assigned_loading_layer ?? 1,
            // Nothing is being edited on a lookahead - every part here is kept exactly as
            // last decided, so its orientation_index is still valid and must be carried
            // forward (omitting it makes the engine default to -1, see partialRenest.utils).
            orientation_index: part.orientation_index,
            transform_matrix: part.transform_matrix ?? IDENTITY_MATRIX,
          }));
    },
    [layerData, currentLayer, scenePartsMap]
  );

  // Decides whether to report success, stop retrying, or auto-chain into the next lookahead batch
  // based on which (if any) of the still-undecided parts the server actually placed.
  const resolveLookaheadOutcome = useCallback(
    (updatedPlan: any, remainingPoolPartIds: Set<string>) => {
      try {
        const freshNestedParts = updatedPlan.nestingOutput?.nested_parts;
        const flatFreshParts = flattenPlacableNestedParts(
          Array.isArray(freshNestedParts) ? freshNestedParts : [],
        );
        // Sort by loading sequence (assigned_loading_layer and order_number).
        const newlyPlaced = sortNestedParts(
          flatFreshParts.filter((part: any) => part?.part_id && remainingPoolPartIds.has(part.part_id)),
        );

        if (newlyPlaced.length > 0) {
          consecutiveEmptyAutoBatchesRef.current = 0;
          lastBatchFirstPartIdRef.current = newlyPlaced[0].part_id;
        } else if (consecutiveEmptyAutoBatchesRef.current + 1 >= MAX_CONSECUTIVE_EMPTY_AUTO_BATCHES) {
          // Stop retrying if all batches in a row returned excluded.
          consecutiveEmptyAutoBatchesRef.current = 0;
          lastBatchFirstPartIdRef.current = null;
          enqueueSnackbar(
            'Could not place the remaining parts automatically - check trailer capacity or edit a part to make room.',
            { variant: 'warning', autoHideDuration: 8000 },
          );
        } else {
          // Auto-chain into next batch if everything was excluded.
          consecutiveEmptyAutoBatchesRef.current += 1;
          pendingRenestTriggerRef.current = { type: 'lookahead', isAutoChained: true };
        }
      } catch (error) {
        console.error('Failed to determine the next lookahead target from the nesting result:', error);
        lastBatchFirstPartIdRef.current = null;
      }
    },
    [lastBatchFirstPartIdRef]
  );

  const submitRenestTrigger = useCallback(
    async (trigger: RenestTrigger) => {
      if (isRenestInFlightRef.current) {
        // A newer trigger (edit or lookahead) supersedes whatever's already queued.
        pendingRenestTriggerRef.current = trigger;
        return;
      }

      // A real user action (an edit, or a lookahead reached by actually navigating there) means
      // the user is making progress - forgive any earlier run of auto-chained empty batches so a
      // fresh edit gets its own full set of retries rather than inheriting a near-exhausted count.
      if (!(trigger.type === 'lookahead' && trigger.isAutoChained)) {
        consecutiveEmptyAutoBatchesRef.current = 0;
      }

      isRenestInFlightRef.current = true;
      setIsRecalculating(true);

      let updatedPlan: any = null;

      const activePlan = currentLoadingPlanRef.current;
      const loadingPlanId = activePlan?.id;
      const rawNestedParts = activePlan?.nestingOutput?.nested_parts;

      // Wrapped end-to-end so any unexpected error - a malformed engine response, a bad payload,
      // whatever - can never leave isRenestInFlightRef/isRecalculating stuck forever with
      // nothing shown to the user. Falls through to the shared tail cleanup below either way.
      try {
        if (loadingPlanId && rawNestedParts) {
          const payload = buildRenestPayload(trigger, rawNestedParts);

          if (payload.length === 0) {
            console.warn('Re-nesting skipped: could not build fixed payload.');
          } else {
            // The full catalog, not just nested_parts/excluded_parts: a part that's never been
            // attempted by any windowed call yet won't appear in either list, so there'd be no way
            // to discover it as "still needs deciding" without the complete included-parts set.
            const includedPartIds = (activePlan?.includedParts ?? []).map(
              (part: any) => `${part.sanitizedName}_${part.globalId}`,
            );
            // Decided = locked/preplaced this call (including parts stacked on a locked pallet, via
            // buildDecidedPartIds' child_items flattening), plus whatever's already excluded within
            // this same post-edit rebuild - editing invalidates everything downstream, so nothing
            // further back than that carries over.
            const decidedPartIds = buildDecidedPartIds(
              payload,
              rawNestedParts,
              (activePlan?.nestingOutput?.excluded_parts ?? []) as { part_id: string }[],
            );
            const undecidedPartIds = getUndecidedPartIds(includedPartIds, decidedPartIds);
            const remainingPoolPartIds = new Set(undecidedPartIds);
            const nestingLimit = Math.min(DEFAULT_NESTING_BATCH_SIZE, undecidedPartIds.length);

            if (nestingLimit === 0 && trigger.type !== 'edit') {
              // Nothing left to place, and a lookahead has no user-driven change of its own to
              // validate either - skip the network round-trip entirely.
              lastBatchFirstPartIdRef.current = null;
            } else {
              // Validate-only edit: no remaining unattempted parts, but user placement must be validated.
              const isValidateOnlyEdit = nestingLimit === 0;
              if (isValidateOnlyEdit) {
                lastBatchFirstPartIdRef.current = null;
              }

              try {
                // Fetch latest updatedAt from server before submitting to avoid stale polling baseline.
                const { updatedAt: requestedBeforeAt } = await loadingPlansService.fetchLoadingPlanById(loadingPlanId);

                await loadingPlansService.requestNesting({
                  loadingPlanId,
                  nested_parts: payload,
                  // Use -1 for validate-only edits so backend treats it as partial (isPartial: !!limit).
                  nesting_limit: isValidateOnlyEdit ? -1 : nestingLimit,
                });

                updatedPlan = await waitForPartialRenestCompletion(loadingPlanId, requestedBeforeAt);
              } catch (error) {
                console.error('Failed to submit re-nesting input:', error);
                enqueueSnackbar('Failed to send the updated loading order to the server.', { variant: 'error' });
              }

              // Discard result if superseded by newer trigger or user is actively editing.
              const wasSupersededExternally = !!pendingRenestTriggerRef.current || isEditModeRef.current;

              if (updatedPlan && !wasSupersededExternally && !isValidateOnlyEdit) {
                resolveLookaheadOutcome(updatedPlan, remainingPoolPartIds);
              }

              if (wasSupersededExternally) {
                updatedPlan = null;
              }
            }
          }
        }
      } catch (error) {
        console.error('Unexpected error while re-nesting:', error);
        enqueueSnackbar('Something went wrong while recalculating the loading plan.', { variant: 'error' });
        updatedPlan = null;
      }

      const nextTrigger = pendingRenestTriggerRef.current;
      pendingRenestTriggerRef.current = null;
      // Release in-flight flag before processing next queued trigger.
      isRenestInFlightRef.current = false;

      if (updatedPlan) {
        currentLoadingPlanRef.current = updatedPlan;
        updateCurrentLoadingPlan({ ...updatedPlan });
        resetSceneForReload(currentPartRef.current?.uuid);
        if (!nextTrigger) {
          enqueueSnackbar('Loading plan recalculated.', { variant: 'success' });
        }
      }

      if (nextTrigger) {
        void submitRenestTriggerRef.current?.(nextTrigger);
        return;
      }

      setIsRecalculating(false);
    },
    [
      buildRenestPayload,
      resolveLookaheadOutcome,
      updateCurrentLoadingPlan,
      resetSceneForReload,
      waitForPartialRenestCompletion,
      currentPartRef,
      isRenestInFlightRef,
      submitRenestTriggerRef,
      lastBatchFirstPartIdRef,
    ]
  );

  submitRenestTriggerRef.current = submitRenestTrigger;

  // Clear any in-flight recalculation polling on unmount.
  useEffect(() => {
    const cancellers = activePollCancellersRef.current;
    return () => {
      cancellers.forEach((cancel) => cancel());
      cancellers.clear();
    };
  }, []);

  return {
    isRecalculating,
    submitRenestTrigger,
    buildUpdatedNestedParts,
  };
}
